import { useState } from 'react'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { api } from '@/lib/api'
import { cn } from '@/lib/cn'
import { formatDate, formatDateTime, formatWeekday, todayInMalaysia } from '@/lib/date'
import type { PlannedJobRow, SectionLoadCell, SectionLoadGrid, SectionLoadLevel } from '@/types'
import Card, { SectionLabel } from '@/components/ui/Card'
import Button from '@/components/ui/Button'
import Modal from '@/components/ui/Modal'
import { Table, Row, Cell, EmptyRow } from '@/components/ui/Table'
import { StateBadge } from '@/components/ui/Badge'
import LogoLoader from '@/components/ui/LogoLoader'
import UrgentBadge from '@/components/job/UrgentBadge'

const SPAN_DAYS = 14

/**
 * Status tokens in the Icewall tinted recipe. Fill level is never carried by
 * colour alone: every cell also prints its pieces and percent, and the legend
 * names each band.
 */
const LEVEL_CLASS: Record<SectionLoadLevel, string> = {
  none: 'border-line text-foreground',
  empty: 'border-line text-muted',
  green: 'border-success/60 bg-success/15 text-success',
  yellow: 'border-warning/60 bg-warning/20 text-warning',
  orange: 'border-pending/60 bg-pending/20 text-pending',
  red: 'border-danger/60 bg-danger/20 text-danger',
}

/** How a plan date was chosen, in the admin's words. */
const BASIS_LABEL: Record<string, string> = {
  scheduled: 'Fitted in',
  oversized: 'Own day - larger than capacity',
  overflow: 'No room - quietest day',
  exempt: 'Quota bypass',
  backfill: 'Carried over',
  fitted: 'Fitted to the delivery date',
}

/** Calendar maths on a Y-m-d string, immune to the browser's timezone. */
function addDays(iso: string, days: number): string {
  const date = new Date(`${iso}T00:00:00Z`)
  date.setUTCDate(date.getUTCDate() + days)
  return date.toISOString().slice(0, 10)
}

/**
 * The section load grid, TRACK's Production Volume report rebuilt: pieces
 * booked into each section per day against its daily capacity. Click a cell
 * to see the jobs behind it.
 */
export default function SectionLoad() {
  const [from, setFrom] = useState(todayInMalaysia)
  const to = addDays(from, SPAN_DAYS - 1)
  const [cell, setCell] = useState<{ departmentId: number; department: string; date: string } | null>(null)

  const grid = useQuery({
    queryKey: ['section-load', from, to],
    queryFn: async () =>
      (await api.get<{ data: SectionLoadGrid }>('/admin/capacity/sections', { params: { from, to } })).data.data,
    placeholderData: keepPreviousData,
  })

  const jobs = useQuery({
    queryKey: ['section-load-jobs', cell?.departmentId, cell?.date],
    queryFn: async () =>
      (
        await api.get<{ data: PlannedJobRow[] }>('/admin/capacity/sections/jobs', {
          params: { department_id: cell!.departmentId, date: cell!.date },
        })
      ).data.data,
    enabled: Boolean(cell),
  })

  if (grid.isLoading || !grid.data) return <LogoLoader />

  const { days, sections, thresholds } = grid.data

  return (
    <div>
      <Card className="mb-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <SectionLabel className="mb-0">Booked pieces per section</SectionLabel>
            <p className="text-xs text-muted">
              {formatDate(from)} to {formatDate(to)} - sections with no capacity set show pieces only
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" aria-label="Previous two weeks" onClick={() => setFrom(addDays(from, -SPAN_DAYS))}>
              <ChevronLeft className="size-4" />
            </Button>
            <Button variant="outline" onClick={() => setFrom(todayInMalaysia())}>
              Today
            </Button>
            <Button variant="ghost" aria-label="Next two weeks" onClick={() => setFrom(addDays(from, SPAN_DAYS))}>
              <ChevronRight className="size-4" />
            </Button>
          </div>
        </div>

        <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5 text-xs">
          <Legend level="green" label={`Up to ${thresholds.green}%`} />
          <Legend level="yellow" label={`${thresholds.green}-${thresholds.yellow}%`} />
          <Legend level="orange" label={`${thresholds.yellow}-${thresholds.orange}%`} />
          <Legend level="red" label="Over capacity" />
          <span className="inline-flex items-center gap-1.5 text-muted">
            <span className="size-3 rounded border border-dashed border-danger/60" /> Closed day with work still booked
          </span>
        </div>
      </Card>

      <div className={cn(grid.isFetching && 'opacity-60 transition-opacity')}>
        <Table
          columns={[
            'Section',
            'Capacity',
            ...days.map((day) => (
              <span key={day.date} className={cn('block whitespace-nowrap text-center', !day.working && 'text-danger')}>
                {formatWeekday(day.date).slice(0, 3)}
                <span className="block font-normal">{formatDate(day.date).slice(0, 5)}</span>
                {!day.working && <span className="block font-normal">closed</span>}
              </span>
            )),
          ]}
        >
          {sections.length === 0 && <EmptyRow colSpan={days.length + 2}>No sections configured.</EmptyRow>}
          {sections.map((section) => (
            <Row key={section.department_id}>
              <Cell className="whitespace-nowrap font-semibold">{section.department}</Cell>
              <Cell className="whitespace-nowrap text-muted">{section.capacity ?? 'Unlimited'}</Cell>
              {section.cells.map((c, index) => (
                <Cell key={c.date} className="p-1 sm:p-1.5">
                  <LoadCell
                    cell={c}
                    capacity={section.capacity}
                    closed={!days[index].working}
                    label={`${section.department}, ${formatWeekday(c.date)} ${formatDate(c.date)}`}
                    onOpen={() => setCell({ departmentId: section.department_id, department: section.department, date: c.date })}
                  />
                </Cell>
              ))}
            </Row>
          ))}
        </Table>
      </div>

      <Modal
        open={Boolean(cell)}
        onClose={() => setCell(null)}
        width="max-w-3xl"
        title={cell ? `${cell.department} - ${formatWeekday(cell.date)} ${formatDate(cell.date)}` : ''}
      >
        {jobs.isLoading ? (
          <LogoLoader />
        ) : (
          <Table columns={['Job', 'Order', 'Customer', 'Pcs', 'How booked', 'Status', 'Section done']}>
            {(jobs.data?.length ?? 0) === 0 && <EmptyRow colSpan={7}>No jobs booked here.</EmptyRow>}
            {jobs.data?.map((job) => (
              <Row key={job.job_id} onClick={() => window.open(`/admin/jobs/${job.job_id}/sheet`, '_blank', 'noopener')}>
                <Cell className="whitespace-nowrap font-mono text-xs font-bold">
                  {job.code}
                  <UrgentBadge urgent={job.is_urgent} className="ml-1.5" />
                </Cell>
                <Cell className="whitespace-nowrap">{job.order_number}</Cell>
                <Cell>{job.customer ?? '-'}</Cell>
                <Cell className="font-semibold">{job.pieces}</Cell>
                <Cell className="text-xs">{BASIS_LABEL[job.basis] ?? job.basis}</Cell>
                <Cell>{job.status && <StateBadge state={job.status} />}</Cell>
                <Cell className={cn('whitespace-nowrap text-xs', job.late && 'font-semibold text-status-danger')}>
                  {job.completed_at ? formatDateTime(job.completed_at) : job.late ? 'Late' : '-'}
                </Cell>
              </Row>
            ))}
          </Table>
        )}
      </Modal>
    </div>
  )
}

function LoadCell({
  cell,
  capacity,
  closed,
  label,
  onOpen,
}: {
  cell: SectionLoadCell
  capacity: number | null
  closed: boolean
  label: string
  onOpen: () => void
}) {
  const percent = capacity ? Math.round((cell.pieces / capacity) * 100) : null
  const summary = capacity
    ? `${cell.pieces} of ${capacity} pcs (${percent}%), ${cell.jobs} job${cell.jobs === 1 ? '' : 's'}`
    : `${cell.pieces} pcs, ${cell.jobs} job${cell.jobs === 1 ? '' : 's'}`

  if (cell.pieces === 0) {
    return (
      <span
        className={cn(
          'flex h-11 min-w-14 items-center justify-center rounded-lg border text-xs text-muted',
          closed ? 'border-dashed border-line' : 'border-line',
        )}
        aria-label={`${label}: nothing booked`}
      >
        -
      </span>
    )
  }

  return (
    <button
      type="button"
      onClick={onOpen}
      title={`${label}: ${summary}`}
      aria-label={`${label}: ${summary}`}
      className={cn(
        'flex h-11 w-full min-w-14 flex-col items-center justify-center rounded-lg border px-1 text-center transition-colors hover:brightness-95',
        LEVEL_CLASS[cell.level],
        closed && 'border-dashed border-danger/60',
      )}
    >
      <span className="text-xs font-bold leading-tight">
        {cell.pieces}
        {capacity !== null && <span className="font-normal">/{capacity}</span>}
      </span>
      {percent !== null && <span className="text-2xs leading-tight">{percent}%</span>}
    </button>
  )
}

function Legend({ level, label }: { level: SectionLoadLevel; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-muted">
      <span className={cn('size-3 rounded border', LEVEL_CLASS[level])} /> {label}
    </span>
  )
}
